import type React from "react";
import type {
  AiProviderId,
  OnboardingPermissionId,
  OnboardingPermissionStatus,
  OnboardingSurface,
  TraderProfile,
} from "../../lib/onboarding-v2";

export type WelcomeStep = "profile" | "permissions" | "desk" | "ai";
export type BusyState = "idle" | "loading" | "saving" | "connecting";

export interface WelcomeOnboardingProps {
  apiBase?: string;
  forceRestart?: boolean;
  getAccessToken: () => Promise<string | null>;
  isDeskReady: boolean;
  onEnter: () => void;
  surface?: OnboardingSurface;
  userEmail?: string | null;
}

export interface PermissionOption {
  id: OnboardingPermissionId;
  label: string;
  reason: string;
}

export interface ProviderOption {
  id: AiProviderId;
  label: string;
  detail: string;
  needsKey: boolean;
}

export interface ProfileStepProps {
  busy: BusyState;
  fileRef: React.RefObject<HTMLInputElement | null>;
  isBusy: boolean;
  onAvatar: (file: File | null) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onUpdate: <K extends keyof TraderProfile>(
    key: K,
    value: TraderProfile[K],
  ) => void;
  profile: TraderProfile;
}

export interface PermissionsStepProps {
  busy: BusyState;
  isBusy: boolean;
  onContinue: () => void;
  onRequest: (id: OnboardingPermissionId) => void;
  onSkip: (id: OnboardingPermissionId) => void;
  statuses: Partial<Record<OnboardingPermissionId, OnboardingPermissionStatus>>;
}

export interface DeskStepProps {
  busy: BusyState;
  isBusy: boolean;
  onCreateDesk: (name: string) => void;
  onJoinPic: () => void;
  profile: TraderProfile;
}

export interface ProviderStepProps {
  apiKey: string;
  busy: BusyState;
  isBusy: boolean;
  oauthCommand: string | null;
  onApiKeyChange: (value: string) => void;
  onConfirmGrok: () => void;
  onProviderChange: (provider: AiProviderId) => void;
  onSubmit: () => void;
  provider: AiProviderId;
  surface: OnboardingSurface;
}

export const stepOrder: WelcomeStep[] = [
  "profile",
  "permissions",
  "desk",
  "ai",
];

export const permissionOptions: PermissionOption[] = [
  {
    id: "notifications",
    label: "Notifications",
    reason: "Desk invites, approvals, and mobile push.",
  },
  {
    id: "microphone",
    label: "Microphone",
    reason: "Voice command and PsychAssist sessions.",
  },
  {
    id: "camera",
    label: "Camera",
    reason: "Chart, screen, and visual context capture.",
  },
];

export const providerOptions: ProviderOption[] = [
  {
    id: "hermes-grok",
    label: "Grok via X",
    detail: "Use your Grok subscription through Hermes OAuth.",
    needsKey: false,
  },
  {
    id: "hermes-set-model",
    label: "Hermes-set Agent",
    detail: "Use the model already selected in Hermes.",
    needsKey: false,
  },
  {
    id: "deepseek-direct",
    label: "DeepSeek API",
    detail: "Bring your DeepSeek API key for CAO chat.",
    needsKey: true,
  },
  {
    id: "opencode-go",
    label: "OpenCode Go API",
    detail: "Use your OpenCode Go API key.",
    needsKey: true,
  },
];
